/**
 * Cross-platform user storage utility
 * Works for both web (localStorage) and desktop (Electron userData)
 */

export interface UserStorage {
  get<T>(key: string): T | null;
  set<T>(key: string, value: T): void;
  remove(key: string): void;
  clear(): void;
}

// Check if we're in an Electron environment
const isElectron = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    (window as any).process &&
    (window as any).process.type === 'renderer'
  );
};

// Check if we're in a web environment
const isWeb = (): boolean => {
  return typeof window !== 'undefined' && !isElectron();
};

/**
 * Web storage implementation using localStorage
 */
class WebStorage implements UserStorage {
  get<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(`Failed to get item from localStorage: ${key}`, error);
      return null;
    }
  }

  set<T>(key: string, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Failed to set item in localStorage: ${key}`, error);
    }
  }

  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove item from localStorage: ${key}`, error);
    }
  }

  clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Failed to clear localStorage', error);
    }
  }
}

/**
 * Electron storage implementation using IPC to main process
 */
class ElectronStorage implements UserStorage {
  private async callMainProcess<T>(method: string, ...args: any[]): Promise<T> {
    if (!(window as any).electronAPI) {
      throw new Error('Electron API not available');
    }

    return (window as any).electronAPI.invoke('user-storage', { method, args });
  }

  get<T>(key: string): T | null {
    // For Electron, we need to make this synchronous for the interface
    // We'll use a synchronous approach with a fallback
    try {
      // Try to get from a cache first (if available)
      const cacheKey = `electron-cache-${key}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        return JSON.parse(cached);
      }

      // If no cache, return null and let the caller handle async loading
      return null;
    } catch (error) {
      console.error(`Failed to get item from Electron storage: ${key}`, error);
      return null;
    }
  }

  set<T>(key: string, value: T): void {
    // For Electron, we'll cache locally and sync in background
    try {
      const cacheKey = `electron-cache-${key}`;
      sessionStorage.setItem(cacheKey, JSON.stringify(value));

      // Sync to main process in background
      this.callMainProcess<void>('set', key, value).catch((error) => {
        console.error(`Failed to sync to Electron storage: ${key}`, error);
      });
    } catch (error) {
      console.error(`Failed to set item in Electron storage: ${key}`, error);
    }
  }

  remove(key: string): void {
    try {
      const cacheKey = `electron-cache-${key}`;
      sessionStorage.removeItem(cacheKey);

      // Sync to main process in background
      this.callMainProcess<void>('remove', key).catch((error) => {
        console.error(
          `Failed to sync removal to Electron storage: ${key}`,
          error,
        );
      });
    } catch (error) {
      console.error(
        `Failed to remove item from Electron storage: ${key}`,
        error,
      );
    }
  }

  clear(): void {
    try {
      // Clear local cache
      const keys = Object.keys(sessionStorage);
      keys.forEach((key) => {
        if (key.startsWith('electron-cache-')) {
          sessionStorage.removeItem(key);
        }
      });

      // Sync to main process in background
      this.callMainProcess<void>('clear').catch((error) => {
        console.error('Failed to sync clear to Electron storage', error);
      });
    } catch (error) {
      console.error('Failed to clear Electron storage', error);
    }
  }
}

/**
 * Fallback storage implementation (in-memory)
 */
class FallbackStorage implements UserStorage {
  private storage = new Map<string, any>();

  get<T>(key: string): T | null {
    return this.storage.get(key) || null;
  }

  set<T>(key: string, value: T): void {
    this.storage.set(key, value);
  }

  remove(key: string): void {
    this.storage.delete(key);
  }

  clear(): void {
    this.storage.clear();
  }
}

/**
 * Create the appropriate storage implementation based on environment
 */
export function createUserStorage(): UserStorage {
  if (isWeb()) {
    return new WebStorage();
  } else if (isElectron()) {
    return new ElectronStorage();
  } else {
    console.warn('Unknown environment, using fallback storage');
    return new FallbackStorage();
  }
}

/**
 * Global storage instance
 */
export const userStorage = createUserStorage();

/**
 * Theme-specific storage keys
 */
export const THEME_STORAGE_KEY = 'flik-theme';

/**
 * Theme storage utilities
 */
export const themeStorage = {
  get(): 'light' | 'dark' | null {
    const result = userStorage.get<'light' | 'dark'>(THEME_STORAGE_KEY);
    return result === 'light' || result === 'dark' ? result : null;
  },

  set(theme: 'light' | 'dark'): void {
    userStorage.set(THEME_STORAGE_KEY, theme);
  },

  remove(): void {
    userStorage.remove(THEME_STORAGE_KEY);
  },
};
